"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { servicesIcons } from "@/constant/services-icons";
import { digitsEnToFa } from "@persian-tools/persian-tools";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import axiosInstance from "@/lib/axios";

import { getOrders } from "@ndpco/services";
import { serviceOrderStatuses, serviceOrderStatusesMapsToVariants } from "@ndpco/types";
import { Badge } from "@ndpco/ui/components/Badge";
import { Button } from "@ndpco/ui/components/Button";

type Props = {
  isMobile: boolean;
};

const ActiveOrder = ({ isMobile }: Props) => {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const activeOrderQuery = useQuery({
    queryKey: ["getActiveOrders"],
    queryFn: async () =>
      await getOrders(axiosInstance, {
        only_actives: 1,
        only_mine: 1,
        per_page: 1,
        with: ["lawyer", "lawyer_user"],
      }),
  });

  if (
    pathname.startsWith("/auth") ||
    pathname.startsWith("/profile") ||
    pathname.startsWith("/lawyer-profile/") ||
    pathname.startsWith("/fa/") ||
    pathname.startsWith("/chat/") ||
    activeOrderQuery.isLoading ||
    activeOrderQuery.isError ||
    !activeOrderQuery.data
  )
    return null;

  const orders = activeOrderQuery.data.data.data.orders.data;

  return (
    <>
      <div aria-hidden="true" className="h-14 md:hidden"></div>
      <div className="fixed inset-x-0 bottom-[calc(theme(spacing.16)+theme(spacing.4)+env(safe-area-inset-bottom))] z-20 w-full sm:inset-x-auto sm:bottom-8 sm:left-8 sm:max-w-sm">
        {orders.map((order) => (
          <div
            key={order.id}
            onClick={() => setOpen(!open)}
            data-open={open}
            className="shadow-card group ring-opacity-50 hover:ring-opacity-15 mx-4 h-14 overflow-y-clip rounded-md bg-white px-2 py-2 ring-4 ring-zinc-300 transition-all ease-linear hover:ring-4 hover:ring-zinc-400 data-[open=true]:h-max sm:mx-0 sm:ring-0"
          >
            <div className="relative flex flex-col gap-y-3 before:invisible before:absolute before:inset-0 before:z-0 before:ms-[calc(theme(spacing.5)-2px)] before:h-full before:w-1 before:bg-zinc-200 group-data-[open=true]:before:visible">
              <div className="relative flex items-start justify-between gap-x-6 bg-white pb-1">
                <div className="relative flex items-center gap-x-2">
                  <div
                    className="flex size-10 items-center justify-center rounded-lg text-white"
                    style={{ backgroundColor: `var(--service-${order.service})` }}
                  >
                    {servicesIcons[order.service]}
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-semibold">
                      {order._service__title} {order.package && order.package_title}{" "}
                      {order.category && order.category.title}
                    </div>
                    <div className="text-xs text-zinc-700">
                      {digitsEnToFa(
                        dayjs
                          .utc(order.created_at)
                          .tz("Asia/Tehran")
                          .format("dddd D MMMM YYYY ساعت HH:mm")
                      )}
                    </div>
                  </div>
                </div>
                <Badge variant={serviceOrderStatusesMapsToVariants[order.status]}>
                  {serviceOrderStatuses[order.status].title.fa}
                </Badge>
              </div>
              <div className="invisible relative flex items-center gap-x-2 bg-white pt-1 group-data-[open=true]:visible group-data-[open=true]:h-auto">
                <div className="relative size-[var(--avatar-size)] shrink-0 overflow-hidden rounded-lg shadow-inner [--avatar-size:theme('spacing.10')]">
                  {order.lawyer.user.avatar_file?.url ? (
                    <Image
                      src={order.lawyer.user.avatar_file.url}
                      fill
                      className="size-[var(--avatar-size)] object-cover"
                      alt={`${order.lawyer.user.first_name} ${order.lawyer.user.last_name}`}
                    />
                  ) : (
                    <div className="bg-zinc-200 text-zinc-500">
                      <svg
                        className="fill-current text-5xl font-semibold uppercase select-none"
                        viewBox="0 0 100 100"
                        aria-hidden="true"
                      >
                        <text
                          x="50%"
                          y="50%"
                          alignmentBaseline="middle"
                          dominantBaseline="middle"
                          textAnchor="middle"
                          // dy=".125em"
                        >
                          {order.lawyer.user.last_name[0]}
                        </text>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="text-sm font-semibold">{`${order.lawyer.user.first_name} ${order.lawyer.user.last_name}`}</div>
                  <div className="text-xs text-zinc-700">{order.lawyer.title}</div>
                </div>
              </div>
              <div className="invisible relative bg-white pt-1 group-data-[open=true]:visible group-data-[open=true]:h-auto">
                <Link href={`/profile/orders/${order.id}`} prefetch={false}>
                  <Button variant="outline" className="w-full">
                    مشاهده سفارش
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ActiveOrder;
